import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { addHours, format } from "date-fns";
import { Music2, User, Building, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { formatMoney } from "@/lib/format";

const DEFAULT_DURATIONS = [60, 90, 120, 180, 240];

export default function BookingWizard({ onSuccess, onCancel, defaultClientId, prefillService }) {
    const [step, setStep] = useState(1);
    const [serviceId, setServiceId] = useState(prefillService || "");
    const [roomId, setRoomId] = useState("");
    const [engineerId, setEngineerId] = useState("");
    const [duration, setDuration] = useState(120);
    const [quantity, setQuantity] = useState(1);
    const [startDate, setStartDate] = useState(format(addHours(new Date(), 24), "yyyy-MM-dd"));
    const [startTime, setStartTime] = useState("14:00");
    const [clientId, setClientId] = useState(defaultClientId || "");
    const [notes, setNotes] = useState("");
    const [rankedEngineers, setRankedEngineers] = useState([]);
    const [availability, setAvailability] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => apiGet("/services") });
    const { data: rooms = [] } = useQuery({ queryKey: ["rooms"], queryFn: () => apiGet("/rooms") });
    const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: () => apiGet("/staff") });
    const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => apiGet("/clients") });

    const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
    const durationOptions = useMemo(() => {
        const min = Number(service?.min_duration_min ?? 60);
        const max = Number(service?.max_duration_min ?? 480);
        const options = DEFAULT_DURATIONS.filter((durationOption) => durationOption >= min && durationOption <= max);
        return options.length ? options : [min];
    }, [service]);
    const engineerCandidates = useMemo(() => {
        if (!service?.requires_engineer) return [];
        if (rankedEngineers.length) return rankedEngineers;
        const requiredSpecialties = (service.required_specialties?.length ? service.required_specialties : [service.type]).filter(Boolean).map((value) => String(value).toLowerCase());
        const allowedStaff = service.staff_ids || service.qualified_staff_ids || [];
        return staff
            .filter((person) => {
                if (person.active === false) return false;
                if (allowedStaff.length && !allowedStaff.includes(person.id)) return false;
                if (person.room_permissions?.length && roomId && !person.room_permissions.includes(roomId)) return false;
                const specialties = (person.specialties || []).map((value) => String(value).toLowerCase());
                return !requiredSpecialties.length || requiredSpecialties.some((specialty) => specialties.includes(specialty));
            })
            .map((person) => ({ ...person, available: null, availabilityUnknown: true, reasons: ["Availability will be checked for the selected time."] }));
    }, [rankedEngineers, roomId, service, staff]);
    const compatibleRooms = useMemo(() => {
        if (!service) return rooms;
        return rooms.filter((r) => !r.supported_services?.length || r.supported_services.includes(service.type));
    }, [rooms, service]);

    const startIso = useMemo(() => {
        try { return new Date(`${startDate}T${startTime}:00Z`).toISOString(); } catch { return null; }
    }, [startDate, startTime]);
    const endIso = useMemo(() => startIso ? new Date(new Date(startIso).getTime() + duration * 60000).toISOString() : null, [startIso, duration]);

    useEffect(() => {
        setRoomId((r) => (compatibleRooms.find((cr) => cr.id === r) ? r : compatibleRooms[0]?.id || ""));
    }, [compatibleRooms]);

    useEffect(() => {
        if (!service) return;
        const min = Number(service.min_duration_min ?? 60);
        const max = Number(service.max_duration_min ?? 480);
        setDuration((current) => Math.min(max, Math.max(min, current)));
        if (!service.requires_engineer) setEngineerId("");
    }, [service]);

    // Rank engineers when we have service + room + times
    useEffect(() => {
        if (!serviceId || !roomId || !startIso || !endIso) return;
        (async () => {
            try {
                const ranked = await apiPost("/availability/eligible-staff", {
                    service_id: serviceId, room_id: roomId, start_utc: startIso, end_utc: endIso,
                });
                setRankedEngineers(ranked);
            } catch { setRankedEngineers([]); }
        })();
    }, [serviceId, roomId, startIso, endIso]);

    // Availability preview
    useEffect(() => {
        if (!roomId || !startIso || !endIso) return;
        (async () => {
            try {
                const av = await apiPost("/availability/evaluate", { service_id: serviceId, room_id: roomId, start_utc: startIso, end_utc: endIso, engineer_id: engineerId || undefined });
                setAvailability(av);
            } catch { setAvailability(null); }
        })();
    }, [roomId, serviceId, startIso, endIso, engineerId]);

    const room = rooms.find((r) => r.id === roomId);
    const price = useMemo(() => {
        if (!service) return 0;
        if (service.pricing_model === "hourly") return Math.round(service.price * (duration / 60));
        return Math.round(service.price * quantity * 100) / 100;
    }, [service, duration, quantity]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await apiPost("/bookings", {
                client_id: clientId, room_id: roomId, service_id: serviceId,
                quantity, engineer_id: engineerId || undefined,
                start_utc: startIso, end_utc: endIso, notes,
                source: "wizard", status: "pending",
            });
            if (res?.code) {
                toast.error(res.detail || "Booking conflict", { description: res.suggestions?.length ? `${res.suggestions.length} alternatives available.` : undefined });
            } else {
                toast.success("Booking created", { description: `Deposit due: ${formatMoney(res.deposit_required)}` });
                onSuccess?.(res);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create booking");
        } finally {
            setSubmitting(false);
        }
    };

    const canNextFromStep = (s) => {
        if (s === 1) return !!serviceId;
        if (s === 2) return !!roomId;
        if (s === 3) return service && (!service.requires_engineer || !!engineerId);
        if (s === 4) return !!startIso && !!endIso;
        if (s === 5) return !!clientId;
        return true;
    };

    const StepDot = ({ num, label }) => (
        <div className="flex items-center gap-2">
            <div className={`h-6 w-6 rounded-full text-[11px] font-semibold flex items-center justify-center ${step >= num ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{step > num ? <CheckCircle2 className="h-4 w-4" /> : num}</div>
            <span className={`text-xs ${step === num ? "font-medium" : "text-muted-foreground"}`}>{label}</span>
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs">
                <StepDot num={1} label="Service" />
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <StepDot num={2} label="Room" />
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <StepDot num={3} label="Engineer" />
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <StepDot num={4} label="When" />
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <StepDot num={5} label="Client" />
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <StepDot num={6} label="Review" />
            </div>

            {step === 1 && (
                <Card className="p-4 border-border">
                    <div className="flex items-center gap-2 mb-3"><Music2 className="h-4 w-4 text-primary" /><h3 className="font-medium">Choose a service</h3></div>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {services.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => { setServiceId(s.id); setQuantity(1); }}
                                className={`text-left rounded-md border p-3 hover:shadow transition-shadow ${serviceId === s.id ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                                data-testid={`wizard-service-${s.id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium">{s.name}</div>
                                        <div className="text-xs text-muted-foreground">{s.pricing_model === "hourly" ? `${formatMoney(s.price)}/hr` : s.pricing_model === "starting" ? `From ${formatMoney(s.price)}` : `${formatMoney(s.price)} / ${s.unit_label || "unit"}`}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                    {service?.pricing_model === "fixed" && <div className="mt-4 max-w-xs"><Label>Quantity ({service.unit_label || "units"})</Label><Input type="number" min={service.min_quantity ?? 1} max={service.max_quantity ?? 1000} step="1" value={quantity} onChange={(e) => setQuantity(Number(e.target.value) || 1)} data-testid="wizard-quantity" /><div className="mt-1 text-xs text-muted-foreground">{formatMoney(service.price)} per {service.unit_label || "unit"}</div></div>}
                </Card>
            )}

            {step === 2 && (
                <Card className="p-4 border-border">
                    <div className="flex items-center gap-2 mb-3"><Building className="h-4 w-4 text-primary" /><h3 className="font-medium">Pick a room</h3></div>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {compatibleRooms.map((r) => (
                            <button key={r.id} onClick={() => setRoomId(r.id)} className={`text-left rounded-md border p-3 hover:shadow transition-shadow ${roomId === r.id ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`wizard-room-${r.id}`}>
                                <div className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                                    <div className="font-medium">{r.name}</div>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">{formatMoney(r.hourly_rate)}/hr · Cap {r.capacity}</div>
                                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
                            </button>
                        ))}
                    </div>
                </Card>
            )}

            {step === 3 && (
                <Card className="p-4 border-border">
                    <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-primary" /><h3 className="font-medium">{service?.requires_engineer ? "Assign an engineer" : "Engineer (optional)"}</h3></div>
                    {!service?.requires_engineer && (
                        <button onClick={() => { setEngineerId(""); setStep(4); }} className="rounded-md border border-border bg-card p-3 text-sm mb-3" data-testid="wizard-engineer-skip">Skip — no engineer needed</button>
                    )}
                        {engineerCandidates.length === 0 && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">No qualified engineers are configured for this service and room yet. Go back to choose another room or service.</div>}
                        {rankedEngineers.length === 0 && engineerCandidates.length > 0 && <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">No engineer is confirmed for the suggested slot yet. Choose a qualified engineer; availability will be checked after you choose the date and time.</div>}
                        <div className="space-y-2">
                        {engineerCandidates.map((e) => (
                            <button key={e.id} onClick={() => setEngineerId(e.id)} className={`w-full text-left rounded-md border p-3 hover:shadow transition-shadow ${engineerId === e.id ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`wizard-engineer-${e.id}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium">{e.name} <span className="text-xs text-muted-foreground">— {e.role?.replace("_", " ")}</span></div>
                                        <div className="text-xs text-muted-foreground">{e.reasons?.slice(0, 3).join(" · ") || ""}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`text-xs ${e.availabilityUnknown ? "text-muted-foreground" : e.available ? "text-emerald-700" : "text-red-700"}`}>{e.availabilityUnknown ? "Check at selected time" : e.available ? "Available" : "Busy"}</div>
                                        <div className="text-xs text-muted-foreground">★ {e.rating}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                        </div>
                </Card>
            )}

            {step === 4 && (
                <Card className="p-4 border-border space-y-4">
                    <div>
                        <Label>Date</Label>
                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="wizard-date" />
                    </div>
                    <div>
                        <Label>Start time (UTC)</Label>
                        <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="wizard-time" />
                    </div>
                    <div>
                        <Label>Duration</Label>
                        <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                            <SelectTrigger data-testid="wizard-duration"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {durationOptions.map((d) => <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {availability && (
                        <div className={`rounded-md border p-3 text-sm ${availability.available ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="wizard-availability">
                            {availability.available ? "This slot is available." : `Conflict detected. ${availability.alternatives?.length || 0} alternatives found.`}
                            {!availability.available && availability.alternatives?.length > 0 && (
                                <ul className="mt-2 space-y-1">
                                    {availability.alternatives.slice(0, 3).map((a) => (
                                        <li key={a.start}>
                                            <button className="underline" onClick={() => {
                                                const s = new Date(a.start);
                                                setStartDate(format(s, "yyyy-MM-dd"));
                                                setStartTime(format(s, "HH:mm"));
                                            }} data-testid={`wizard-alt-${a.start}`}>Try {format(new Date(a.start), "EEE MMM d, h:mm a")}</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </Card>
            )}

            {step === 5 && (
                <Card className="p-4 border-border">
                    <div className="flex items-center gap-2 mb-3"><User className="h-4 w-4 text-primary" /><h3 className="font-medium">Who’s booking?</h3></div>
                    <Select value={clientId} onValueChange={setClientId}>
                        <SelectTrigger data-testid="wizard-client-select"><SelectValue placeholder="Choose a client" /></SelectTrigger>
                        <SelectContent>
                            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.artist_name ? ` — ${c.artist_name}` : ""}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <div className="mt-4">
                        <Label>Notes</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the engineer should know?" data-testid="wizard-notes" />
                    </div>
                </Card>
            )}

            {step === 6 && (
                <Card className="p-4 border-border space-y-2">
                    <div className="font-display text-lg font-semibold">Review</div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Service:</span> {service?.name}</div>
                        <div><span className="text-muted-foreground">Room:</span> {room?.name}</div>
                        <div><span className="text-muted-foreground">Engineer:</span> {staff.find((s) => s.id === engineerId)?.name || "—"}</div>
                        <div><span className="text-muted-foreground">When:</span> {format(new Date(startIso), "EEE, MMM d yyyy · h:mm a")} ({duration} min)</div>
                        <div><span className="text-muted-foreground">Client:</span> {clients.find((c) => c.id === clientId)?.name}</div>
                        <div><span className="text-muted-foreground">Price:</span> {formatMoney(price)}{service?.pricing_model === "fixed" ? ` (${quantity} ${service.unit_label || "units"})` : ""}</div>
                        <div className="sm:col-span-2"><span className="text-muted-foreground">Deposit required:</span> {formatMoney(Math.round(price * (service?.deposit_pct || 0.5)))}</div>
                    </div>
                </Card>
            )}

            <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" onClick={() => (step === 1 ? onCancel?.() : setStep(step - 1))} data-testid="wizard-back">
                    {step === 1 ? "Cancel" : "Back"}
                </Button>
                {step < 6 ? (
                    <Button onClick={() => setStep(step + 1)} disabled={!canNextFromStep(step)} data-testid="wizard-next">Continue</Button>
                ) : (
                    <Button onClick={submit} disabled={submitting} data-testid="wizard-submit">Create booking</Button>
                )}
            </div>
        </div>
    );
}
